import React from "react";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const FAQS = [
  {
    q: "Qu'est-ce que la préparation mentale ?",
    a: [
      "Comme l'aspect physique ou technique, le mental s'entraîne.",
      "La préparation mentale consiste à développer les compétences mentales qui influencent le bien-être et la performance : confiance en soi, gestion du stress, concentration, motivation, gestion des émotions ou encore capacité à rester présent dans l'instant.",
      "Chaque personne dispose déjà de ressources mentales qui lui sont propres. L'objectif de l'accompagnement est d'apprendre à les comprendre, les mobiliser, les renforcer et les adapter aux situations rencontrées au quotidien ou dans la pratique sportive.",
      "L'objectif n'est pas seulement de performer davantage, mais aussi de retrouver plus de sérénité, de plaisir et de maîtrise dans ses actions.",
    ],
  },
  {
    q: "À qui s'adresse l'accompagnement ?",
    a: [
      "La préparation mentale s'adresse à toute personne souhaitant développer ses ressources mentales pour mieux gérer les défis du quotidien, améliorer son bien-être ou atteindre un objectif personnel ou professionnel.",
      "Elle peut accompagner :",
    ],
    list: [
      "Les sportifs, quel que soit leur niveau (loisir, compétition, haut niveau) pour travailler la confiance en soi, la gestion du stress, la concentration ou la performance.",
      "Les étudiants qui préparent un concours, un examen ou une échéance importante.",
      "Les entrepreneurs, professionnels ou équipes qui souhaitent renforcer leur cohésion, leur gestion de la pression ou leur équilibre dans leur activité.",
    ],
    after: [
      "Il n'existe aucun prérequis pour commencer un accompagnement en préparation mentale : chaque suivi est adapté aux besoins, aux objectifs et au rythme de chacun.",
    ],
  },
  {
    q: "Combien de séances faut-il prévoir ?",
    a: [
      "Il n'existe pas de nombre de séances prédéfini en préparation mentale : chaque accompagnement est personnalisé et construit en fonction des besoins, des objectifs et du rythme de chacun.",
      "La première séance permet de faire connaissance, de comprendre la demande et de définir ensemble les axes de travail. À partir de cet échange, une proposition d'accompagnement peut être construite.",
      "Certaines personnes souhaitent travailler sur un objectif précis en quelques séances (gestion du stress avant une compétition, préparation à un concours, regain de confiance), tandis que d'autres choisissent un accompagnement plus progressif dans le temps.",
      "L'objectif est d'avancer au rythme de chacun, avec un suivi adapté et évolutif.",
    ],
  },
  {
    q: "Préparation mentale et psychologie : quelle différence ?",
    a: [
      "La préparation mentale et le suivi psychologique sont deux accompagnements différents mais complémentaires.",
      "Le suivi psychologique vise à accompagner la compréhension du fonctionnement psychique, des émotions ou de certaines difficultés pouvant impacter le bien-être au quotidien.",
      "La préparation mentale, quant à elle, est orientée vers le développement des compétences mentales utiles pour atteindre un objectif ou mieux faire face à une situation : confiance en soi, gestion du stress, concentration, motivation, gestion des émotions ou préparation à un événement important.",
      "En préparation mentale, l'accompagnement cherche à développer des stratégies et des outils concrets pour favoriser l'épanouissement, le bien-être et la performance dans le sport, les études ou le milieu professionnel.",
      "Ces approches ne s'opposent pas : selon les besoins, elles peuvent être complémentaires.",
    ],
  },
  {
    q: "Peut-on faire des séances à distance ?",
    a: [
      "Oui, il est tout à fait possible de réaliser des séances de préparation mentale à distance, en visioconférence.",
      "Le travail en distanciel est aujourd'hui une solution très efficace et appréciée, car il permet d'être accompagné où que l'on soit, sans contrainte de déplacement, tout en conservant la qualité du suivi et de l'échange.",
      "Toutes les séances individuelles sont proposées à distance, en visioconférence. Les interventions en présentiel peuvent être envisagées pour les séances de groupe ou les interventions en structure.",
      "Ce format permet de rendre la préparation mentale plus accessible, que ce soit pour les sportifs, les étudiants ou les professionnels.",
    ],
  },
  {
    q: "Comment se déroule une première séance ?",
    a: [
      "Chez Performance by Mind, la première séance de 30 minutes est offerte. Elle permet de faire connaissance et de comprendre les besoins, la situation et les objectifs de la personne accompagnée.",
      "Lors de cet échange, nous abordons la raison de la venue, les attentes, ainsi que les objectifs que la personne souhaite travailler en préparation mentale.",
      "La séance suivante (la première séance de travail) permet d'approfondir ces éléments : le parcours, l'environnement, les expériences vécues, ainsi que les ressources déjà en place et les objectifs à court, moyen ou long terme.",
      "L'objectif est de construire un accompagnement personnalisé, adapté à chaque profil et à chaque besoin.",
    ],
  },
];

export const FAQ = () => {
  return (
    <section id="faq" className="relative py-24 md:py-32 bg-white">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-14"
        >
          <span className="text-base md:text-lg font-bold uppercase tracking-[0.18em] text-[#9B8FE0]">
            FAQ
          </span>
          <h2 className="font-display mt-4 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
            Vos questions, mes <span className="text-[#9B8FE0]">réponses</span>.
          </h2>
          <p className="mt-6 text-xl md:text-2xl text-zinc-600 leading-relaxed max-w-3xl">
            Les essentiels pour comprendre la préparation mentale et savoir si
            elle est faite pour vous.
          </p>
        </motion.div>

        <Accordion type="single" collapsible className="w-full space-y-4">
          {FAQS.map((item, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              data-testid={`faq-item-${i}`}
              className="bg-[#FAF6F1] border border-zinc-200 rounded-2xl px-6 md:px-8 hover:border-[#9B8FE0]/50 transition-colors"
            >
              <AccordionTrigger className="font-display text-xl md:text-2xl font-bold tracking-tight py-6 text-left hover:no-underline [&[data-state=open]>svg]:text-[#9B8FE0]">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="pb-6 text-lg md:text-xl text-zinc-700 leading-relaxed space-y-4">
                {item.a.map((p, idx) => (
                  <p key={idx}>{p}</p>
                ))}
                {item.list && (
                  <ul className="space-y-2 pl-1">
                    {item.list.map((li, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#9B8FE0] shrink-0" />
                        <span>{li}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {item.after &&
                  item.after.map((p, idx) => <p key={`a-${idx}`}>{p}</p>)}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className="mt-14 bg-gradient-to-br from-[#9B8FE0] to-[#B5A8EC] text-white rounded-3xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl md:text-3xl font-bold">
              Une autre question ?
            </h3>
            <p className="mt-2 text-lg md:text-xl text-white/90 max-w-xl">
              Réservez votre séance découverte de 30 minutes, offerte.
            </p>
          </div>
          <a
            href="#contact"
            data-testid="faq-cta-contact"
            className="inline-flex items-center gap-2 bg-white text-[#9B8FE0] font-bold text-lg px-7 py-3.5 rounded-full hover:-translate-y-0.5 hover:shadow-lg transition-all"
          >
            Me contacter
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
